import { useCallback, useState, Activity } from 'react';
import {
  type PlantsListViewFilterState,
  PlantsListViewFilterCommand,
} from './plants-list-view-filter-command.tsx';
import { usePlantsViewFilterStore } from '@/services/store/use-plants-view-filter-store.ts';
import { useShallow } from 'zustand/react/shallow';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { PlantsSearchInput } from './plants-search-input.tsx';
import { PlantsArchiveToggle } from './plants-archive-toggle.tsx';
import { PlantsFiltersToggle } from './plants-filters-toggle.tsx';
import { PlantsLayoutToggle } from './plants-layout-toggle.tsx';
import { Button } from '@/components/ui/button.tsx';
import { XIcon } from 'lucide-react';

export const PlantsView = () => {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const filtersVisible = !isMobile || mobileFiltersOpen;

  const [resetKey, setResetKey] = useState(0);
  const { setupType, view, search, setFilterState, setSearch } = usePlantsViewFilterStore(
    useShallow((state) => ({
      setupType: state.setupType,
      view: state.view,
      search: state.search,
      setFilterState: state.setFilterState,
      setSearch: state.setSearch,
    }))
  );

  const hasActiveFilters = setupType !== 'all' || view !== 'overview' || search !== '';

  const handleStateChange = useCallback(
    (newState: PlantsListViewFilterState) => {
      setFilterState(newState);
    },
    [setFilterState]
  );

  const handleClear = useCallback(() => {
    setFilterState({ view: 'overview', setupType: 'all', search: '' });
    setResetKey((k) => k + 1);
  }, [setFilterState]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex w-full gap-2 sm:w-auto">
        <PlantsSearchInput key={resetKey} defaultValue={search} onSearch={setSearch} />
        <PlantsFiltersToggle
          open={filtersVisible}
          onToggle={() => setMobileFiltersOpen((prev) => !prev)}
        />
      </div>
      <Activity mode={filtersVisible ? 'visible' : 'hidden'}>
        <PlantsListViewFilterCommand
          currentState={{
            setupType,
            view,
          }}
          onStateChange={handleStateChange}
        />
        <div className="flex items-center gap-2">
          <PlantsLayoutToggle />
          <PlantsArchiveToggle />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="default"
              onClick={handleClear}
              className="flex items-center gap-1"
            >
              <span>Clear</span>
              <XIcon className="size-4" />
            </Button>
          )}
        </div>
      </Activity>
    </div>
  );
};
